import {
  type AnnotationGeoBox,
  type AnnotationsState,
  type TripAnnotationMarker,
  useAnnotationsState,
} from '@backoffice-frontend/overwatch-map-common'
import type * as maplibregl from 'maplibre-gl'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  type MapMode,
  resetRouteMapCameraSnapshot,
} from './TripEditor/RouteMap'
import type {
  ODDMapElement,
  RouteViolation,
  SdsViolation,
  Waypoint,
} from './TripEditor/types'

type VehicleMarkerData = {
  location: { latitude: number; longitude: number }
  name: string
  isPhantom?: boolean
  heading?: number | null
}

export type MapPadding = {
  top: number
  right: number
  bottom: number
  left: number
}

// Data props — stored in state (changes drive RouteMap re-renders)
export type SharedMapData = {
  center: [number, number]
  mode: MapMode
  waypoints: Waypoint[]
  routeCoordinates: [number, number][]
  violations: RouteViolation[]
  sdsViolations: SdsViolation[]
  fitWaypoints: boolean
  fitKey: string | null
  oddElements: ODDMapElement[]
  /**
   * Raw driver voice notes as fetched, unfiltered. Published here by
   * TripEditorView (the view that owns the query) purely so this provider can
   * hand them to {@link useAnnotationsState}, which owns every filter and the
   * control state — see {@link SharedMapContextValue.annotations}. Nothing reads
   * this field directly to render.
   */
  annotationRows: TripAnnotationMarker[]
  /** Older pages of {@link annotationRows} are still arriving. */
  annotationRowsLoadingMore: boolean
  /** {@link annotationRows} stopped short of the whole list (cap or error). */
  annotationRowsTruncated: boolean
  /**
   * The selected metro's area. Only the notes inside it are counted or drawn,
   * whatever the backend sent. `null` before the metro's origin is known.
   */
  annotationArea: AnnotationGeoBox | null
  vehicleMarker: VehicleMarkerData | undefined
  headings: Record<string, number>
  pendingHeadingWaypointId: string | null
  padding: MapPadding
  /**
   * Whether leaving the editor flies back to the metropolis center. The view
   * that enters editor mode sets it: a fresh Build Route sets `false` (cancel
   * keeps the camera where the user was working), a selected saved route and
   * the edit / duplicate flows set `true`. It is not reset when the editor
   * unmounts, because RouteMap reads it on that very transition.
   */
  recenterOnOverview: boolean
}

// Props forwarded to the always-visible MapOverlayOptions component in the map column
export type SharedOverlayOptionsData = {
  metropolisId: number
  metropolisName?: string
  autoEnableLanes: boolean
  autoEnableGeonets: boolean
  onOddElementClick?: (lat: number, lng: number, type: string) => void
  routeOddElements?: ODDMapElement[]
  vtrRequiredTypes?: string[]
}

// Callback props — stored in refs (never cause re-renders)
export type SharedMapCallbacks = {
  onMapClick: ((lat: number, lng: number) => void) | undefined
  onMarkerClick: ((waypointId: string) => void) | undefined
  onMarkerDrag: ((index: number, lat: number, lng: number) => void) | undefined
  onMarkerDragStart: ((waypointId: string) => void) | undefined
  onMarkerMoveStart: ((waypointId: string) => void) | undefined
  onOddElementClick: ((element: ODDMapElement) => void) | undefined
  onHeadingSelect: ((waypointId: string, bearing: number) => void) | undefined
  onRemoveHeading: ((waypointId: string) => void) | undefined
}

export type SharedMapContextValue = {
  mapData: SharedMapData
  setMapData: (patch: Partial<SharedMapData>) => void
  /** Stable ref to current callbacks — RouteMap reads from this ref directly */
  callbacksRef: React.MutableRefObject<SharedMapCallbacks>
  setCallbacks: (cbs: Partial<SharedMapCallbacks>) => void
  /** The live map instance (null until RouteMap fires onMapReady) */
  mapInstance: maplibregl.Map | null
  /** Called by RouteMap when its map is ready */
  onMapReady: (map: maplibregl.Map) => void
  /** Extra overlay content rendered over the map (e.g. toasts, loading animations) */
  mapOverlay: React.ReactNode
  setMapOverlay: (node: React.ReactNode) => void
  /** Controls whether the details panel is open (drives animation) */
  detailsPanelOpen: boolean
  setDetailsPanelOpen: (open: boolean) => void
  /** Content rendered inside the details panel — update this independently of open state */
  detailsPanel: React.ReactNode
  setDetailsPanel: (node: React.ReactNode) => void
  /** Props for the MapOverlayOptions component — always rendered at map top-right */
  overlayOptions: SharedOverlayOptionsData | null
  setOverlayOptions: (opts: SharedOverlayOptionsData | null) => void
  /**
   * The annotation layer's filters, counts and control state, owned by
   * `useAnnotationsState` in the shared map lib so the trip-history map can run
   * the identical feature without this provider. It lives on the context rather
   * than inside TripEditorView because the Annotations button is rendered by
   * RouteBuilderAreaContentView — a sibling of TripEditorView under this same
   * provider — and would otherwise need prop drilling through every
   * TripEditorView variant (create / create-from-vtr / edit / assign) to reach
   * it. TripEditorView still owns fetching, and publishes the raw rows as
   * {@link SharedMapData.annotationRows}.
   */
  annotations: AnnotationsState
  /**
   * Which of the two map-rail dropdowns (Layers, Annotations) is currently
   * open, or neither. Lifted here — rather than left as private state inside
   * MapControls and AnnotationsButton — so opening one can close the other:
   * both read/write the same value, so setting it to one menu implicitly
   * closes whichever menu it wasn't set to.
   */
  openMapMenu: 'layers' | 'annotations' | null
  setOpenMapMenu: (menu: 'layers' | 'annotations' | null) => void
  /**
   * True when the map spans the whole container *under* a flush sidebar (the
   * assign-trip modal), as opposed to sitting beside it as an inset card (the
   * main screen). Shared views (SavedTripsView, TripEditorView) read this to
   * pick the right route-fit padding via `computeMapPadding`.
   */
  fullBleedMap: boolean
}

const DEFAULT_CENTER: [number, number] = [9.993, 53.551]

export const DEFAULT_PADDING: MapPadding = {
  top: 60,
  right: 60,
  bottom: 60,
  left: 60,
}

export const DEFAULT_MAP_DATA: SharedMapData = {
  center: DEFAULT_CENTER,
  mode: 'overview',
  waypoints: [],
  routeCoordinates: [],
  violations: [],
  sdsViolations: [],
  fitWaypoints: false,
  fitKey: null,
  oddElements: [],
  annotationRows: [],
  annotationRowsLoadingMore: false,
  annotationRowsTruncated: false,
  annotationArea: null,
  vehicleMarker: undefined,
  headings: {},
  pendingHeadingWaypointId: null,
  padding: DEFAULT_PADDING,
  recenterOnOverview: true,
}

const DEFAULT_CALLBACKS: SharedMapCallbacks = {
  onMapClick: undefined,
  onMarkerClick: undefined,
  onMarkerDrag: undefined,
  onMarkerDragStart: undefined,
  onMarkerMoveStart: undefined,
  onOddElementClick: undefined,
  onHeadingSelect: undefined,
  onRemoveHeading: undefined,
}

const SharedMapContext = createContext<SharedMapContextValue | null>(null)

export const SharedMapProvider = ({
  children,
  initialCenter,
  fullBleedMap = false,
}: {
  children: React.ReactNode
  initialCenter: [number, number]
  /** See {@link SharedMapContextValue.fullBleedMap}. Defaults to the main screen. */
  fullBleedMap?: boolean
}) => {
  const [mapData, setMapDataRaw] = useState<SharedMapData>({
    ...DEFAULT_MAP_DATA,
    center: initialCenter,
  })
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null)
  const [mapOverlay, setMapOverlay] = useState<React.ReactNode>(null)
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(false)
  const [detailsPanel, setDetailsPanel] = useState<React.ReactNode>(null)
  const [overlayOptions, setOverlayOptions] =
    useState<SharedOverlayOptionsData | null>(null)
  // Every annotation filter and control value, plus the filtered layer itself.
  // The proximity filter measures against the polyline the map is already
  // drawing (`routeCoordinates`), so "near this route" can never disagree with
  // the route on screen. With no route computed yet that is `[]` and the filter
  // stays inert rather than blanking the layer.
  //
  // `within` gates the layer to the selected metro, so the count and the map
  // never include a note from another city, even if the backend sends one.
  const annotations = useAnnotationsState({
    annotations: mapData.annotationRows,
    route: mapData.routeCoordinates,
    within: mapData.annotationArea,
    loadingMore: mapData.annotationRowsLoadingMore,
    truncated: mapData.annotationRowsTruncated,
  })
  const [openMapMenu, setOpenMapMenu] = useState<
    'layers' | 'annotations' | null
  >(null)
  const callbacksRef = useRef<SharedMapCallbacks>({ ...DEFAULT_CALLBACKS })

  const setMapData = useCallback((patch: Partial<SharedMapData>) => {
    setMapDataRaw(prev => ({ ...prev, ...patch }))
  }, [])

  // This provider is the route-builder session boundary. As RouteMap's parent,
  // this cleanup runs AFTER RouteMap saves its camera snapshot on unmount, so
  // clearing it here ensures re-entering the area starts at the default overview
  // rather than resuming the camera of whatever route was open on exit. The
  // snapshot still survives RouteMap remounts within a live session.
  useEffect(() => resetRouteMapCameraSnapshot, [])

  const setCallbacks = useCallback((cbs: Partial<SharedMapCallbacks>) => {
    callbacksRef.current = { ...callbacksRef.current, ...cbs }
  }, [])

  const onMapReady = useCallback((map: maplibregl.Map) => {
    setMapInstance(map)
  }, [])

  return (
    <SharedMapContext.Provider
      value={{
        mapData,
        setMapData,
        callbacksRef,
        setCallbacks,
        mapInstance,
        onMapReady,
        mapOverlay,
        setMapOverlay,
        detailsPanelOpen,
        setDetailsPanelOpen,
        detailsPanel,
        setDetailsPanel,
        overlayOptions,
        setOverlayOptions,
        annotations,
        openMapMenu,
        setOpenMapMenu,
        fullBleedMap,
      }}
    >
      {children}
    </SharedMapContext.Provider>
  )
}

export const useSharedMapContext = () => {
  const ctx = useContext(SharedMapContext)
  if (!ctx)
    throw new Error('useSharedMapContext must be inside SharedMapProvider')
  return ctx
}
